import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Search, Trash2 } from "lucide-react";
import { AuditThumb } from "@/components/admin/audit/AuditThumb";

export default function DuplicatesPanel({ params, categoryId }) {
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState(false);
    const [ignoreSep, setIgnoreSep] = useState(false);
    const [sel, setSel] = useState(new Set());

    const scan = async () => {
        setBusy(true);
        try {
            const { data: d } = await api.get("/admin/audit/duplicates", { params: { ...params(), ignore_separators: ignoreSep } });
            setData(d);
            setSel(new Set());
        } catch (e) {
            toast.error(e.response?.data?.detail || "Skanimi dështoi");
        } finally {
            setBusy(false);
        }
    };

    const toggle = (id) => setSel((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

    const remove = async () => {
        if (!window.confirm(`Do të fshihen përgjithmonë ${sel.size} artikuj. Je i sigurt?`)) return;
        setBusy(true);
        try {
            const { data: r } = await api.post("/admin/audit/delete-products", { ids: [...sel] });
            toast.success(`U fshinë ${r.deleted} artikuj`);
            await scan();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Fshirja dështoi");
            setBusy(false);
        }
    };

    return (
        <section className="space-y-3" data-testid="audit-duplicates">
            <div className="flex flex-wrap items-center gap-3">
                <button onClick={scan} disabled={busy} className="btn-primary" data-testid="audit-dup-scan">
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />} Skano dublikatët
                </button>
                <label className="inline-flex items-center gap-1.5 text-xs font-mono cursor-pointer" data-testid="audit-dup-ignore-sep">
                    <input type="checkbox" checked={ignoreSep} onChange={(e) => setIgnoreSep(e.target.checked)} /> Injoro vizat / hapësirat (5440-970 = 5440970)
                </label>
                {sel.size > 0 && (
                    <button onClick={remove} disabled={busy} className="btn-outline !border-red-300 !text-red-600" data-testid="audit-dup-delete">
                        <Trash2 className="w-4 h-4" /> Fshi të zgjedhurit ({sel.size})
                    </button>
                )}
            </div>
            <p className="text-xs text-slate-500">Krahasohet vetëm <b>Part №</b>{categoryId ? " brenda kategorisë së zgjedhur" : " në të gjithë katalogun"}. Zgjidh me kuti kopjet që do të fshish.</p>
            {data && (
                <div className="text-sm" data-testid="audit-dup-summary">
                    <b>{data.group_count}</b> kode të dublikuara · <b>{data.extra_copies}</b> kopje të tepërta
                </div>
            )}
            {data?.groups.map((g) => (
                <div key={g.key} className="border border-slate-200 bg-white" data-testid={`audit-dup-group-${g.key}`}>
                    <div className="px-3 py-2 border-b border-slate-200 font-mono font-bold text-sm">{g.key} <span className="text-slate-400 font-normal">· {g.items.length} herë</span></div>
                    <table className="w-full text-xs">
                        <tbody>
                            {g.items.map((p) => (
                                <tr key={p.id} className={`border-b border-slate-100 ${sel.has(p.id) ? "bg-red-50" : ""}`}>
                                    <td className="p-2 w-8"><input type="checkbox" checked={sel.has(p.id)} onChange={() => toggle(p.id)} data-testid={`audit-dup-check-${p.id}`} /></td>
                                    <td className="p-2 w-12"><AuditThumb src={p.image_url} /></td>
                                    <td className="p-2"><a href={`/product/${p.id}`} target="_blank" rel="noreferrer" className="font-bold hover:underline">{p.part_number}</a><div className="text-slate-500">{p.name}</div></td>
                                    <td className="p-2">{p.category}</td>
                                    <td className="p-2 font-mono">stok {p.stock_qty}</td>
                                    <td className="p-2 font-mono">usage {p.usage_count}</td>
                                    <td className="p-2 font-mono text-slate-400">{p.created_at ? new Date(p.created_at).toLocaleDateString() : "—"}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            ))}
            {data && data.groups.length === 0 && <div className="text-sm text-emerald-700" data-testid="audit-dup-none">Nuk ka dublikatë.</div>}
        </section>
    );
}
